import type { BookingProposalRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { type IncludedOrderLine } from 'noddi-util';

import { useMemo } from 'react';
import { resolveProposalIncludedOrderLines } from '../utils/proposalIncludedOrderLines';

/**
 * The proposal car's outstanding open-order items, as the garage sales-item
 * picker shows them: the open order's residual lines plus the service the open
 * order locked (that line is hidden from `openOrder.lines` and carries its
 * price instead).
 *
 * Display only — `booking_create_at_site` charges every open-order line onto
 * the new booking's order by itself, so nothing here is sent on create. Without
 * these lines the recall checkout showed a total below what the customer pays.
 */
export function useProposalOpenOrder(proposal: BookingProposalRecord | undefined) {
  // The stationary proposal flow books the first item's car (see the confirm CTA).
  const primaryItem = proposal?.bookingProposalItems[0];
  const licensePlateNumber = primaryItem?.car.licensePlate.number;
  const serviceDepartmentId = proposal?.serviceDepartment.id;
  const userGroupId = proposal?.userGroupMember.userGroup.id;

  // isLoading (not isPending): the query is gated by `enabled`.
  const { data, isLoading } = noddiAsync.useGet({
    type: URLKeys.getStationaryBookableServices,
    input: {
      serviceDepartmentId: serviceDepartmentId!,
      licensePlateNumber: licensePlateNumber!,
      userGroupId
    },
    queryConfig: { enabled: serviceDepartmentId != null && !!licensePlateNumber }
  });

  const proposalSalesItemIds = useMemo(
    () => (primaryItem?.salesItemsWithQuantity ?? []).map(({ salesItem }) => salesItem.id),
    [primaryItem?.salesItemsWithQuantity]
  );

  const includedOrderLines: IncludedOrderLine[] = useMemo(
    () =>
      data
        ? resolveProposalIncludedOrderLines({
            services: data.services,
            lockedSalesItemId: data.lockedSalesItemId,
            openOrder: data.openOrder,
            proposalSalesItemIds
          })
        : [],
    [data, proposalSalesItemIds]
  );

  // Best-effort, like the charge itself: a failed lookup must not block the
  // checkout, so the inclusions are simply absent and the miss is logged.

  return { includedOrderLines, isLoading };
}
